import { useCallback, useEffect, useRef, useState } from "react";
import { AppState, Platform } from "react-native";
import { useReduceMotion } from "../../hooks/useReduceMotion";
import { createHomeRoulette, type HomeRouletteState } from "./homeRoulette";

/** Pass route focus as `active`: navigation can leave Home mounted. */
export function useHomeRoulette(directionCount: number, active: boolean) {
  const [state, setState] = useState<HomeRouletteState>({
    phase: "idle",
    highlighted: null,
    selected: null,
  });
  const controller = useRef<ReturnType<typeof createHomeRoulette> | null>(null);
  const reducedMotion = useReduceMotion();

  useEffect(() => {
    const cycle = createHomeRoulette(directionCount, setState);
    controller.current = cycle;
    setState(cycle.getState());
    return () => {
      cycle.dispose();
      if (controller.current === cycle) controller.current = null;
    };
  }, [directionCount]);

  const cancel = useCallback(() => controller.current?.cancel(), []);
  useEffect(() => {
    if (!active) cancel();
  }, [active, cancel]);

  useEffect(() => {
    if (reducedMotion) controller.current?.finishWithoutMotion();
  }, [reducedMotion]);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (next) => {
      if (next !== "active") cancel();
    });
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return () => subscription.remove();
    }
    const onVisibility = () => {
      if (document.hidden) cancel();
    };
    const onPageHide = () => cancel();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      subscription.remove();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [cancel]);

  const start = useCallback(
    () => active && (controller.current?.start(reducedMotion) ?? false),
    [active, reducedMotion],
  );
  return {
    state,
    start,
    cancel,
    reducedMotion,
    destination: controller.current?.getDestination() ?? null,
  };
}
